import React, { useCallback } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import {
	SpotlightListStepper,
	type SpotlightProps,
} from '@atlassian/jira-servicedesk-itsm-onboarding-common/src/ui/spotlight-list-stepper';
import { useYourWorkTour } from '../../../services';
import { DemoProjectSpotlight } from './demo-project';
import { ItsmSampleProjectSpotlight } from './itsm-sample-project';
import { YourProjectSpotlight } from './your-project';

type Props = {
	withDemoProject: boolean;
};

export const spotlightsWithDemoProject = [
	({ api: { next, exit } }: SpotlightProps) => (
		<YourProjectSpotlight onPrimary={next} onClose={exit} />
	),
	({ api: { complete, back, exit } }: SpotlightProps) => (
		<DemoProjectSpotlight onPrimary={complete} onSecondary={back} onClose={exit} />
	),
];

export const spotlightsWithItsmSampleProject = [
	({ api: { next, exit } }: SpotlightProps) => (
		<YourProjectSpotlight onPrimary={next} onClose={exit} />
	),
	({ api: { complete, back, exit } }: SpotlightProps) => (
		<ItsmSampleProjectSpotlight onPrimary={complete} onSecondary={back} onClose={exit} />
	),
];

const SpotlightTourInner = ({ withDemoProject }: Props) => {
	const [, { next, end }] = useYourWorkTour();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onExit = useCallback(() => {
		end('spotlightTour', createAnalyticsEvent({}));
	}, [createAnalyticsEvent, end]);

	const onComplete = useCallback(() => {
		next('spotlightTour', createAnalyticsEvent({}));
	}, [createAnalyticsEvent, next]);

	return (
		<SpotlightListStepper
			/**
			 * There is an edge case here where we tried and failed to create the ITSM sample project.
			 * In this case we won't be able to spotlight the sample project as intended even though
			 * we show a next button and 1/2 in the bottom left corner.
			 * TODO: fix this edge case with https://jdog.jira-dev.com/browse/JIG-681
			 */
			spotlights={withDemoProject ? spotlightsWithDemoProject : spotlightsWithItsmSampleProject}
			onExit={onExit}
			onComplete={onComplete}
		/>
	);
};

export const SpotlightTour = (props: Props) => {
	const { withDemoProject } = props;
	return (
		<ContextualAnalyticsData attributes={{ withDemoProject }}>
			<SpotlightTourInner {...props} />
		</ContextualAnalyticsData>
	);
};
